import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Tab, Button, Form, Nav } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import TabTableWithSearch from './TabTableWithSearch';
import refreshIcon from "assets/svg/refresh-green.svg";
import { IconButton } from "@mui/material";
import { TimeRangeSelection } from "components/common/TimeRangeDropdown";
import moment from "moment";
import { CHART_CUSTOM_RANGE_OPTION } from "constant";
import { 
    getGatewayAlertNotification,
    getOneRegistry,
    getRegistryConfig,
    getGwAndLDSUStatus,
    getAllLastBusReading,
    getSensorLastReading
} from "service/gatewayService";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { LoadingModal } from "components/modals/ModalTemplate";
import { isHttpSuccess, convertSecondsToPrometheusTime } from "utils/functions";
import { get } from "lodash";
import { useOrganisation } from "hooks/organization";
import { getLDSBusInfo } from "utils/gatewayFunctions";
import { getCalendarFormat } from "utils/datetime";
import {
    queryTagStatusFromUIDSAIDsWithTimestamp,
} from "service/networkService";
import { buildPromQLQueryFromUIDSAIDsMap, selectUIDSAIDsForUID } from "./common/promqlUtils";
import {
    createDefaultLast24HoursTimeRange,
    getPrometheusTimeRangeForSensorReadings,
    pickBestSensorBusReadingForSAID,
    mergeSensorLastReadingWithMinMaxFallback,
} from "./common/sensorStatsUtils";
import {
    formatSensorReadingCellValue,
    formatSensorMinMaxCellValue,
    getSensorUnit,
} from "./common/sensorValueFormatter";
import { fetchTags as fetchTagsCommon } from "./common/tagService";
import {
    getTagDeviceSAID,
    getTagDeviceUID,
    isSensorFromEntityDevice,
    shouldExcludeDeviceFromEntityList,
    normalizeEntityLabel,
    selectEntityUUID,
} from "./common/deviceUtils";
import { getSensorsControllersTabType, renderOnOffHighlightedCommon } from "./common/tabUtils";
import { renderSensorsControllersFilters } from "./common/filters";
import { getControllerStatusInfo } from "./common/controllerStatus";
import { getControllerStatusFromReading2 } from "./common/controllerReadingUtils";
import { getRowToForContext, type NetworkRowToContext } from "./common/getRowTo";
import { 
    updateAlertStatus as updateAlertStatusCommon,
    returnCheckboxStatus as returnCheckboxStatusCommon,
    returnDisabledStatus as returnDisabledStatusCommon
} from "./common/alertUtils";
import { BREADCRUMB_LABELS } from "./constants";
import { DynamicBreadcrumb } from "./common/DynamicBreadcrumb";
import { createSensorNameRenderer } from "./common/sensorRenderers";
import { renderStatusCellCommon, renderLastActiveCellCommon } from "./common/statusRenderer";
import { getNetworkColumnLabel } from "./common/columnLabelUtils";

const renderSensorReadingCell = (row: any, col: string) => {
    const formattedValue = formatSensorReadingCellValue(row, row[col]);
    return <span style={{ color: '#00d1b2' }}>{formattedValue}</span>;
};

const renderSensorMinMaxCell = (row: any, col: string) => (
    <span>
        {row[col] ?? '-'}
    </span>
);

const renderControllerNameCell = (
    row: any,
    tabKey: string,
    handleRowClick: (row: any, tabKey: string) => void,
    getRowTo?: (row: any, tabKey: string) => string
) => {
    const to = getRowTo?.(row, tabKey) ?? "#";
    return (
        <Link
            to={to}
            onClick={to === "#" ? (e) => {
                e.preventDefault();
                handleRowClick(row, tabKey);
            } : undefined}
        >
            {row.name}
        </Link>
    );
};

const renderLastActiveCell = (
    row: any,
    col: string,
    tabKey: string,
    handleRefreshSensor: (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        sensor: any
    ) => void | Promise<void>
) =>
    renderLastActiveCellCommon({
        row,
        col,
        tabKey,
        onRefreshSensor: handleRefreshSensor,
    });

const renderStatusCell = (
    row: any,
    col: string,
    tabKey: string,
    renderOnOffHighlighted: (text: string) => React.ReactNode,
    createRefreshControllerHandler: (rowName: string) =>
        (e: React.MouseEvent<HTMLButtonElement>) => void
) => {
    const statusValue = row[col];
    return renderStatusCellCommon({
        statusValue,
        tabKey,
        controllerName: row.name,
        onRefreshController: (e, controllerName) =>
            createRefreshControllerHandler(controllerName)(
                e as React.MouseEvent<HTMLButtonElement>
            ),
        renderOnOffHighlighted,
        refreshButtonExtraClassName: "refresh-controller",
    });
};

const renderAlertCell = (
    row: any,
    returnCheckboxStatus: (source: string, said: number) => boolean,
    returnDisabledStatus: (source: string, said: number) => boolean,
    createAlertOnChangeHandler: (source: string, said: number) => () => void
) => (
    <HoverAuthorizeTooltip permission="gateway:update">
        <div>
            <Form.Check
                type="switch"
                id={`switch-${row.ldsu_uuid}-${row.SAID}`}
                label=""
                checked={returnCheckboxStatus(
                    row.ldsu_uuid,
                    row.SAID
                )}
                disabled={returnDisabledStatus(
                    row.ldsu_uuid,
                    row.SAID
                )}
                onChange={createAlertOnChangeHandler(row.ldsu_uuid, row.SAID)}
            />
        </div>
    </HoverAuthorizeTooltip>
);

type ColumnRenderDeps = {
    handleRowClick: (row: any, tabKey: string) => void;
    getRowTo?: (row: any, tabKey: string) => string;
    handleRefreshSensor: (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        sensor: any
    ) => void | Promise<void>;
    renderOnOffHighlighted: (text: string) => React.ReactNode;
    createRefreshControllerHandler: (rowName: string) =>
        (e: React.MouseEvent<HTMLButtonElement>) => void;
    returnCheckboxStatus: (source: string, said: number) => boolean;
    returnDisabledStatus: (source: string, said: number) => boolean;
    createAlertOnChangeHandler: (source: string, said: number) => () => void;
};

const buildColumnRenderHelper = (
    col: string,
    tabKey: string,
    deps: ColumnRenderDeps
): ((row: any) => React.ReactNode) | undefined => {
    const colLower = col.toLowerCase();

    if ((colLower === 'name' || colLower === 'sensor_name') && tabKey === 'sensors') {
        const nameRenderer = createSensorNameRenderer(tabKey, deps.handleRowClick, deps.getRowTo);
        return (row: any) => nameRenderer(row);
    }

    if (colLower === 'reading' && tabKey === 'sensors') {
        return (row: any) => renderSensorReadingCell(row, col);
    }

    if ((colLower === 'minmax' || colLower.includes('minmax')) && tabKey === 'sensors') {
        return (row: any) => renderSensorMinMaxCell(row, col);
    }

    if (colLower === 'name' && tabKey === 'controllers') {
        return (row: any) =>
            renderControllerNameCell(row, tabKey, (clickedRow, clickedTab) => {
                deps.handleRowClick(clickedRow, clickedTab);
            }, deps.getRowTo);
    }

    if (colLower === 'lastactive' || colLower.includes('lastactive')) {
        return (row: any) =>
            renderLastActiveCell(row, col, tabKey, deps.handleRefreshSensor);
    }

    if (colLower === 'status') {
        return (row: any) =>
            renderStatusCell(
                row,
                col,
                tabKey,
                deps.renderOnOffHighlighted,
                deps.createRefreshControllerHandler
            );
    }

    if (colLower === 'alert') {
        return (row: any) =>
            renderAlertCell(
                row,
                deps.returnCheckboxStatus,
                deps.returnDisabledStatus,
                deps.createAlertOnChangeHandler
            );
    }

    return undefined;
};

const tabKeys = [
    "sensors",
    "controllers",
];

const tabLabels: { [key: string]: string } = {
    sensors: "Sensors in this Room",
    controllers: "Actuators in this Room",
};

interface RoomDetailsProps {
    // Optional props to make the component more flexible
    networkId?: string;
    zoneId?: string;
    roomId?: string;
    showBreadcrumbs?: boolean;
    customTitle?: string;
}

const RoomDetails = ({ 
    networkId, 
    zoneId, 
    roomId, 
    showBreadcrumbs = true,
    customTitle 
}: RoomDetailsProps): JSX.Element => {
    const params = useParams<{ id: string; zoneId: string; roomId: string }>();
    
    // Use props if provided, otherwise use URL params
    const id = networkId ?? params.id;
    const zoneIdFromParams = zoneId ?? params.zoneId;
    const roomIdFromParams = roomId ?? params.roomId;
    
    const [activeTab, setActiveTab] = useState("sensors");
    const [network, setNetwork] = useState<any>(null);
    const [room, setRoom] = useState<any>(null);
    const [roomName, setRoomName] = useState("");
    const [isRoomNameEditing, setIsRoomNameEditing] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [sensors, setSensors] = useState<any[]>([]);
    const [controllers, setControllers] = useState<any[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const history = useHistory();
    const { currentOrgId } = useOrganisation();
    const roomTagUIDSAIDsRef = useRef<Map<string, Set<number>>>(new Map());
    const { validateAndRedirect } = useNetworkIdRedirect(id);
    
    // Sensor-specific state
    const [selectedSensorTypes, setSelectedSensorTypes] = useState<string[]>([]);
    const [selectedActuatorTypes, setSelectedActuatorTypes] = useState<string[]>([]);
    const timeRange = useMemo<TimeRangeSelection>(
        () => createDefaultLast24HoursTimeRange(),
        []
    );

    // Alert state management
    const [alertUUIDList, setAlertUUIDList] = useState<any[]>([]);
    const [isAlertLoading, setIsAlertLoading] = useState(false);
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();

    // Helper to render a timestamp in a consistent calendar format
    const renderDateTime = useCallback((timestamp?: number | string): string => {
        if (!timestamp) {
            return "";
        }
        return moment(timestamp, "X").calendar(getCalendarFormat());
    }, []);
    
    // Process sensors for display
    const processedSensors = useMemo(() => {
        return sensors.map((sensor: any) => {
            // Extract minmax from sensor to avoid duplicate column
            const { minmax, ...sensorWithoutMinmax } = sensor;
            const ldsuUUID = sensor.ldsu_uuid || sensor.UID || "";
            const saidValue = sensor.SAID || 0;
            const resolvedUnit = getSensorUnit(sensor);
            return {
                // Display fields
                name: sensor.sensor_name ?? sensor.name ?? "-",
                sensor_name: sensor.sensor_name ?? sensor.name ?? "-",
                sensor_type: sensor.sensor_type ?? sensor.type ?? "-",
                type: sensor.sensor_type ?? sensor.type ?? "-",
                // Raw reading value; formatting will be handled by the table renderer
                reading: sensor.readings?.value ?? "-",
                readingSortValue:
                    sensor.readings?.value != null &&
                    sensor.readings?.value !== "-" &&
                    !Number.isNaN(Number(sensor.readings.value))
                        ? Number(sensor.readings.value)
                        : Number.POSITIVE_INFINITY,
                minMax: sensor.readings?.minmax
                    ? formatSensorMinMaxCellValue({
                        readings: sensor.readings,
                        accuracy: sensor.accuracy,
                        unit: resolvedUnit,
                    })
                    : "-",
                minMaxSortValue: (() => {
                    const arr = sensor.readings?.minmax;
                    if (!arr || !Array.isArray(arr) || arr.length < 1) return Number.POSITIVE_INFINITY;
                    const v = arr[0];
                    if (v == null || v === "-" || Number.isNaN(Number(v))) return Number.POSITIVE_INFINITY;
                    return Number(v);
                })(),
                lastActive: renderDateTime(sensor.readings?.timestamp),
                ldsu_uuid: ldsuUUID,
                SAID: saidValue,
                ldsu_name: sensor.ldsu_name || sensor.name || "-",
                alert: "",
                // For alert column sorting: 1 if enabled, 0 if disabled
                alertSortValue: returnCheckboxStatusCommon(
                    alertUUIDList as any,
                    ldsuUUID,
                    saidValue
                )
                    ? 1
                    : 0,
                accuracy: sensor.accuracy,
                unit: resolvedUnit,
                readings: sensor.readings,
                ...sensorWithoutMinmax
            };
        });
    }, [sensors, renderDateTime, alertUUIDList]);

    // Filter sensors by selected types
    const filteredSensorList = useMemo(() => {
        if (!processedSensors.length || selectedSensorTypes.length === 0) {
            return processedSensors;
        }
        return processedSensors.filter(sensor => 
            selectedSensorTypes.includes(sensor.type)
        );
    }, [processedSensors, selectedSensorTypes]);
    
    // Sensor types for filtering
    const sensorTypes = useMemo(() => {
        if (activeTab !== 'sensors' || !processedSensors || processedSensors.length === 0) return [];
        const types: { [id: string]: string } = {};
        for (const row of processedSensors) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [processedSensors, activeTab]);

    // Derive actuator types for filtering
    const actuatorTypes = useMemo(() => {
        if (!controllers.length) return [];
        const types: { [id: string]: string } = {};
        for (const row of controllers) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [controllers]);

    // Filter controllers by selected actuator types
    const filteredControllers = useMemo(() => {
        if (!controllers.length || selectedActuatorTypes.length === 0) {
            return controllers;
        }
        return controllers.filter((actuator: any) =>
            selectedActuatorTypes.includes(actuator.type)
        );
    }, [controllers, selectedActuatorTypes]);

    // Process controllers for display (status from last bus reading only)
    const processedControllers = useMemo(() => {
        return filteredControllers.map((actuator: any) => {
            const { status } = getControllerStatusInfo(actuator);
            const lastActiveTs = actuator.readings?.timestamp ?? actuator.timestamp;
            return {
                name: actuator.name || "-",
                type: actuator.type || "-",
                status,
                lastActive: renderDateTime(lastActiveTs),
                UID: actuator.UID,
                said: actuator.said,
            };
        });
    }, [filteredControllers, renderDateTime]);

    // Helper to toggle alert switch with wallet check
    const handleAlertToggle = (ldsuUUID: string, SAID: number) => {
        const isChecked = returnCheckboxStatus(ldsuUUID, SAID);
        const saidKey = String(SAID);

        if (isChecked) {
            updateAlertStatus(ldsuUUID, saidKey);
            return;
        }

        walletModalTrigger(() => updateAlertStatus(ldsuUUID, saidKey));
    };

    // Helper to create alert onChange handler (avoids deep inline nesting)
    const createAlertOnChangeHandler = (ldsuUUID: string, SAID: number) => () => {
        handleAlertToggle(ldsuUUID, SAID);
    };

    // Helper to create controller refresh click handler (avoids deep inline nesting)
    const createRefreshControllerHandler = (rowName: string) =>
        (e: React.MouseEvent<HTMLButtonElement>) => {
            handleRefreshController(e, rowName);
        };

    // Alert management functions
    const fetchAlertData = useCallback(async () => {
        const ldsuGatewayId = id; // Use panlNetworkId from route params
        
        try {
            const alertResponse = await getGatewayAlertNotification(ldsuGatewayId);
            if (isHttpSuccess(alertResponse.status)) {
                const alerts = alertResponse.data || [];
                // Transform alert data to match sensor structure (device_id -> source, enabled -> status)
                const transformedAlerts = alerts.map((alert: any) => ({
                    uuid: alert.uuid,
                    source: alert.device_id,
                    said: alert.said,
                    gatewayUUID: alert.gateway_id,
                    status: alert.enabled,
                    notice: alert.notice,
                }));
                setAlertUUIDList(transformedAlerts);
            }
        } catch (error) {
            console.error("Error fetching alert data:", error);
        }
    }, [id]);

    const updateAlertStatus = async (source: string, said: string) => {
        await updateAlertStatusCommon(
            alertUUIDList as any,
            source,
            said,
            setAlertUUIDList as any,
            setIsAlertLoading
        );
    };

    const returnCheckboxStatus = (source: string, said: number) => {
        return returnCheckboxStatusCommon(alertUUIDList as any, source, said);
    };

    const returnDisabledStatus = (source: string, said: number) => {
        return returnDisabledStatusCommon(alertUUIDList as any, source, said);
    };

    /**
     * Fetches tags from API
     * @param organizationId - Organization ID
     * @param tagValue - Optional tag value filter (e.g., room name)
     * @returns Array of tag objects
     */
    const fetchTags = useCallback(
        async (organizationId: string, tagValue?: string) =>
            fetchTagsCommon(organizationId, { value: tagValue }),
        []
    );

    const extractTagUIDs = useCallback((tags: any[]): Set<string> => {
        const uids = new Set<string>();
        for (const tag of tags) {
            if (tag.devices && Array.isArray(tag.devices)) {
                for (const device of tag.devices) {
                    const uid = getTagDeviceUID(device);
                    if (uid) {
                        uids.add(uid);
                    }
                }
            }
        }
        return uids;
    }, []);

    /**
     * Extracts UID-SAID pairs from tag API devices array
     * Tag API format: { devices: [["UID", SAID], ...] }
     * @param tags - Array of tag objects from API
     * @returns Map of UID -> Set of SAIDs
     */
    const addDeviceToUidSaidMap = (
        uidSaidMap: Map<string, Set<number>>,
        device: any
    ) => {
        const uid = getTagDeviceUID(device);
        if (!uid) {
            return;
        }

        const said = getTagDeviceSAID(device);
        if (!uidSaidMap.has(uid)) {
            uidSaidMap.set(uid, new Set<number>());
        }
        uidSaidMap.get(uid)!.add(said);
    };

    const extractTagUIDSAIDs = useCallback((tags: any[]): Map<string, Set<number>> => {
        const uidSaidMap = new Map<string, Set<number>>();

        for (const tag of tags) {
            if (!tag.devices || !Array.isArray(tag.devices)) {
                continue;
            }

            for (const device of tag.devices) {
                addDeviceToUidSaidMap(uidSaidMap, device);
            }
        }

        return uidSaidMap;
    }, []);

    /**
     * Extracts device UIDs from registry API response
     * @param registryData - Registry API response data
     * @returns Set of device UIDs
     */
    const extractRegistryUIDs = useCallback((registryData: any): Set<string> => {
        const uids = new Set<string>();
        if (registryData?.info?.devices && Array.isArray(registryData.info.devices)) {
            for (const device of registryData.info.devices) {
                if (device?.UID) {
                    uids.add(String(device.UID));
                } else if (Array.isArray(device) && device.length > 0) {
                    uids.add(String(device[0]));
                } else if (typeof device === 'string') {
                    uids.add(device);
                }
            }
        }
        return uids;
    }, []);

    // Helper: Find intersection of room tag UIDs and registry UIDs
    const findRoomUIDs = useCallback((roomTagUIDs: Set<string>, registryUIDs: Set<string>): Set<string> => {
        const roomUIDs = new Set<string>();
        for (const uid of Array.from(roomTagUIDs)) {
            if (registryUIDs.has(uid)) {
                roomUIDs.add(uid);
            }
        }
        return roomUIDs;
    }, []);

    // Helper: Collect sensors and actuators from all buses
    const collectBusDevices = useCallback((devices: any[], configList: any[], statusDevices: any[]): { sensors: any[]; actuators: any[] } => {
        const allSensors: any[] = [];
        const allActuators: any[] = [];

        for (let busNumber = 1; busNumber <= 3; busNumber++) {
            const { sensors: busSensors, actuators: busActuators } = getLDSBusInfo(
                busNumber.toString(),
                devices,
                configList,
                statusDevices
            );

            // Filter out sensors from zone/room/group/hub entity devices and from auxiliary hub/meter devices,
            // so Room Details sensor list matches the counts used in NetworkDetails.
            const actualSensors = busSensors.filter((s: any) => {
                if (isSensorFromEntityDevice(s, devices)) {
                    return false;
                }
                const device = devices.find(
                    (d: any) => String(d?.UID ?? d?.uid ?? "") === String(s.ldsu_uuid)
                );
                if (!device) {
                    return true;
                }
                if (shouldExcludeDeviceFromEntityList(device)) {
                    return false;
                }
                return true;
            });
            // At room level, trust per‑channel CLS/type from getLDSBusInfo /
            // getActuatorList and do not re‑filter by device OBJ. This avoids
            // hiding valid actuator channels on auxiliary hubs (e.g. LDSBus 2CH Relay).
            const filteredActuators = busActuators;

            allSensors.push(...actualSensors);
            allActuators.push(...filteredActuators);
        }

        return { sensors: allSensors, actuators: allActuators };
    }, []);

    // (Deprecated) Helper: Fetch and map sensor readings without time range
    // Kept for reference but no longer used now that timeRange-aware mapSensorsWithReadings is in place.

    // Helper: Build UID to device map (includes OBJ, NAME, NICK for identification)
    const buildUIDToDeviceMap = useCallback((devices: any[]): Map<string, any> => {
        const uidToDeviceMap = new Map<string, any>();
        for (const device of devices) {
            const deviceUID = device?.UID || device?.uid || "";
            if (deviceUID) {
                uidToDeviceMap.set(String(deviceUID), device);
            }
        }
        return uidToDeviceMap;
    }, []);

    // Helper: Filter room sensors (exclude only entity devices; include sensors from auxiliary hub devices e.g. LDSBus 4in1)
    const filterRoomSensors = useCallback((
        sensorsWithReadings: any[],
        roomUIDs: Set<string>,
        roomTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return sensorsWithReadings.filter((sensor: any) => {
            const sensorUID = sensor.ldsu_uuid || sensor.UID || "";
            const sensorSAID = sensor.SAID !== undefined && sensor.SAID !== null ? Number(sensor.SAID) : 0;
            
            if (!roomUIDs.has(sensorUID)) {
                return false;
            }
            
            const allowedSAIDs = roomTagUIDSAIDs.get(sensorUID);
            if (allowedSAIDs && !allowedSAIDs.has(sensorSAID)) {
                return false;
            }
            
            const device = uidToDeviceMap.get(sensorUID);
            return !shouldExcludeDeviceFromEntityList(device);
        });
    }, []);

    // Helper: Filter room actuators (exclude only entity devices)
    const filterRoomActuators = useCallback((
        allActuators: any[],
        roomUIDs: Set<string>,
        roomTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return allActuators.filter((actuator: any) => {
            const actuatorUID = actuator.UID || "";
            const actuatorSAID = actuator.said !== undefined && actuator.said !== null ? Number(actuator.said) : 0;
            
            if (!roomUIDs.has(actuatorUID)) {
                return false;
            }
            
            const allowedSAIDs = roomTagUIDSAIDs.get(actuatorUID);
            if (allowedSAIDs && allowedSAIDs.size > 0 && !allowedSAIDs.has(actuatorSAID)) {
                return false;
            }
            
            const device = uidToDeviceMap.get(actuatorUID);
            return !shouldExcludeDeviceFromEntityList(device);
        });
    }, []);

    // Helper: Query room status (with timestamp) using UID/SAID-based query
    const queryRoomStatusHelper = useCallback(
        async (
            gatewayId: string,
            roomUuid?: string | null
        ): Promise<{ status: string; timestamp: number | null }> => {
            try {
                const roomUIDSAIDsForStatus = selectUIDSAIDsForUID(roomTagUIDSAIDsRef.current, roomUuid);
                const result = await queryTagStatusFromUIDSAIDsWithTimestamp(
                    gatewayId,
                    (gw: string) =>
                        buildPromQLQueryFromUIDSAIDsMap(gw, roomUIDSAIDsForStatus)
                );
                return {
                    status: result.status,
                    timestamp: result.timestamp ?? null,
                };
            } catch (error) {
                console.error("Error querying room status:", error);
                return { status: "Unknown", timestamp: null };
            }
        },
        []
    );

    // Map readings to sensors (helper function for refresh and initial load)
    const mapSensorsWithReadings = useCallback(
        async (sensorsList: any[], timeRange?: string, offset?: string) => {
            if (!id || !sensorsList.length) return sensorsList;
            
            try {
                const readingPromises = [];
                for (let busNumber = 1; busNumber <= 3; busNumber++) {
                    readingPromises.push(
                        getAllLastBusReading(
                            id,
                            busNumber.toString(),
                            timeRange,
                            offset
                        )
                    );
                }
                const readingResults = await Promise.all(readingPromises);
                const allReadings: any[] = [];
                for (const result of readingResults) {
                    if (isHttpSuccess(result.status)) {
                        allReadings.push(...(result.data?.devices || []));
                    }
                }

                return sensorsList.map((sensor: any) => {
                    const bestReading = pickBestSensorBusReadingForSAID(
                        sensor,
                        allReadings
                    );

                    if (bestReading) {
                        sensor.readings = {
                            value: bestReading.value,
                            highest: bestReading.highest,
                            lowest: bestReading.lowest,
                            timestamp: bestReading.timestamp,
                            minmax: [bestReading.highest, bestReading.lowest],
                        };
                    }

                    return sensor;
                });
            } catch (error) {
                console.error("RoomDetails: Error mapping sensor readings:", error);
                return sensorsList;
            }
        },
        [id]
    );

    // Fetch room data
    const fetchRoomData = useCallback(async () => {
        if (!id || id === 'undefined' || !roomIdFromParams || !currentOrgId) {
            setIsPageLoading(false);
            return;
        }

        try {
            setIsPageLoading(true);
            const expectedPrefix = "room/";
            const expectedTagValue = `${expectedPrefix}${roomIdFromParams}`;
            const rawRoomTags = await fetchTags(currentOrgId, expectedTagValue);

            // Backend value filter can return multiple room tags when names are similar
            // (e.g. "Room_1" and "room_1234"). Post-filter here so this page only uses
            // the tag that actually matches the current room, using normalized labels.
            const expectedNormalizedName = normalizeEntityLabel(roomIdFromParams);
            const roomTags = Array.isArray(rawRoomTags)
                ? rawRoomTags.filter((tag: any) => {
                    const tagValue: string = tag?.value ?? "";
                    if (!tagValue.toLowerCase().startsWith(expectedPrefix)) {
                        return false;
                    }
                    const tagNamePart = tagValue.slice(expectedPrefix.length);
                    return normalizeEntityLabel(tagNamePart) === expectedNormalizedName;
                })
                : [];

            const roomTagUIDs = extractTagUIDs(roomTags);
            const roomTagUIDSAIDs = extractTagUIDSAIDs(roomTags);
            roomTagUIDSAIDsRef.current = roomTagUIDSAIDs;
            
            const [registryRes, configRes, statusRes] = await Promise.all([
                getOneRegistry(id),
                getRegistryConfig(id),
                getGwAndLDSUStatus(id)
            ]);

            if (!isHttpSuccess(registryRes.status) || 
                !isHttpSuccess(configRes.status) || 
                !isHttpSuccess(statusRes.status)) {
                setIsPageLoading(false);
                return;
            }

            const devices = registryRes.data?.info?.devices || [];
            const configList = configRes.data || [];
            const statusDevices = statusRes.data?.devices || [];
            const registryUIDs = extractRegistryUIDs(registryRes.data);
            
            const roomUIDs = findRoomUIDs(roomTagUIDs, registryUIDs);
            const { sensors: allSensors, actuators: allActuators } = collectBusDevices(devices, configList, statusDevices);

            const selectedTimeRange = timeRange;
            let lastTimeValue = Math.round(
                moment.duration(selectedTimeRange.endDate.diff(selectedTimeRange.startDate)).asSeconds()
            );
            let offsetTime: string | undefined;

            if (selectedTimeRange.range === CHART_CUSTOM_RANGE_OPTION) {
                lastTimeValue = Math.round(
                    moment.duration(moment().diff(selectedTimeRange.endDate)).asSeconds()
                );

                offsetTime = convertSecondsToPrometheusTime(lastTimeValue);
                lastTimeValue = moment
                    .duration(selectedTimeRange.endDate.diff(selectedTimeRange.startDate))
                    .asSeconds();
            }

            const lastTime = convertSecondsToPrometheusTime(lastTimeValue);

            // Fetch readings for sensors using the current time range
            const sensorsWithReadings = await mapSensorsWithReadings(
                allSensors,
                lastTime,
                offsetTime
            );
            const uidToDeviceMap = buildUIDToDeviceMap(devices);
            
            const roomSensors = filterRoomSensors(sensorsWithReadings, roomUIDs, roomTagUIDSAIDs, uidToDeviceMap);
            const roomActuators = filterRoomActuators(allActuators, roomUIDs, roomTagUIDSAIDs, uidToDeviceMap);
            const roomUuid = selectEntityUUID(roomUIDs, devices, roomIdFromParams, "room");

            let roomStatus = "Unknown";
            let roomTimestamp: number | null = null;
            if (currentOrgId && id && roomIdFromParams) {
                const { status, timestamp } = await queryRoomStatusHelper(
                    id,
                    roomUuid
                );
                roomStatus = status;
                roomTimestamp = timestamp ?? null;
            }

            const roomData = {
                name: roomIdFromParams,
                uuid: roomUuid,
                sensors: roomSensors.length,
                controllers: roomActuators.length,
                status: roomStatus,
                // Store the time when the room status was last evaluated
                timestamp: roomTimestamp,
            };

            setRoom(roomData);
            setRoomName(roomIdFromParams);
            setSensors(roomSensors);
            // Merge actuator status from getAllLastBusReading (same source as refresh)
            const mergeActuatorWithDevices = (a: any, devices: any[]) => {
                const device = devices.find((d: any) => d.UID === a.UID);
                if (!device) return a;
                const status = get(device.value, `[${a.said || 0}]`, null);
                return { ...a, readings: { status, timestamp: device.ts } };
            };
            const mergeActuatorsWithReadings = async (actuatorsList: any[]) => {
                const readingPromises = [];
                for (let busNumber = 1; busNumber <= 3; busNumber++) {
                    readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
                }
                const results = await Promise.all(readingPromises);
                const allDevices: any[] = [];
                for (const r of results) {
                    if (isHttpSuccess(r.status) && r.data?.devices) allDevices.push(...r.data.devices);
                }
                return actuatorsList.map((a) => mergeActuatorWithDevices(a, allDevices));
            };
            const roomActuatorsWithReadings = await mergeActuatorsWithReadings(roomActuators);
            setControllers(roomActuatorsWithReadings);

            setNetwork({
                name: registryRes.data?.name || "Network",
                id: id
            });

            await fetchAlertData();
            setIsPageLoading(false);
        } catch (error) {
            console.error("Error fetching room data:", error);
            setIsPageLoading(false);
        }
    }, [
        id,
        roomIdFromParams,
        currentOrgId,
        fetchAlertData,
        fetchTags,
        extractTagUIDs,
        extractTagUIDSAIDs,
        extractRegistryUIDs,
        findRoomUIDs,
        collectBusDevices,
        mapSensorsWithReadings,
        buildUIDToDeviceMap,
        filterRoomSensors,
        filterRoomActuators,
        queryRoomStatusHelper,
        timeRange,
    ]);

    // Dedicated function to update only the status of the room
    const updateRoomStatus = useCallback(async () => {
        if (!id || !roomIdFromParams || !currentOrgId) {
            return;
        }

        setIsRefreshing(true);
        try {
            const { status, timestamp } = await queryRoomStatusHelper(
                id,
                room?.uuid
            );
            setRoom((prevRoom: any) => {
                if (!prevRoom) return prevRoom;
                return {
                    ...prevRoom,
                    status,
                    // Update timestamp from backend where available, otherwise keep previous
                    timestamp: timestamp ?? prevRoom.timestamp ?? null,
                };
            });
        } catch (error) {
            console.error("Error updating room status:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, roomIdFromParams, currentOrgId, queryRoomStatusHelper, room]);

    useEffect(() => {
        setIsPageLoading(true);
        fetchRoomData();
    }, [fetchRoomData]);

    // Refresh all sensor readings
    const refreshAllSensorReadings = useCallback(async (timeRange?: string, offset?: string) => {
        if (!id || !sensors.length) return;
        
        setIsRefreshing(true);
        try {
            const sensorsWithReadings = await mapSensorsWithReadings(
                sensors,
                timeRange,
                offset
            );
            setSensors(sensorsWithReadings);
        } catch (error) {
            console.error("RoomDetails: Error refreshing sensor readings:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, sensors, mapSensorsWithReadings, setIsRefreshing, setSensors]);

    // Refresh a single sensor's reading using getSensorLastReading
    const refreshOneSensor = async (ldsuUUID: string, SAID: number) => {
        if (!id || !ldsuUUID) return;

        try {
            const { lastTime, offsetTime } =
                getPrometheusTimeRangeForSensorReadings(timeRange);
            const { data, status } = await getSensorLastReading(
                id,
                ldsuUUID,
                lastTime,
                offsetTime
            );

            if (isHttpSuccess(status)) {
                const rawValueCandidateRaw = get(data.value, `[${SAID}]`, "-");
                const highestCandidateRaw = get(data.max, `[${SAID}]`, "-");
                const lowestCandidateRaw = get(data.min, `[${SAID}]`, "-");

                const valueCandidate = rawValueCandidateRaw ?? "-";
                const highestCandidate = highestCandidateRaw ?? "-";
                const lowestCandidate = lowestCandidateRaw ?? "-";

                setSensors((prevSensors: any[]) => {
                    return prevSensors.map((s: any) => {
                        if (String(s.ldsu_uuid) === String(ldsuUUID) && String(s.SAID) === String(SAID)) {
                            const prevReadings = s.readings ?? {};

                            const updatedReading =
                                mergeSensorLastReadingWithMinMaxFallback(
                                    prevReadings,
                                    {
                                        highest: highestCandidate,
                                        lowest: lowestCandidate,
                                        value: valueCandidate,
                                        timestamp: data?.ts,
                                    }
                                );

                            return { ...s, readings: updatedReading };
                        }
                        return s;
                    });
                });
            }
        } catch (error) {
            console.error("RoomDetails: Error refreshing sensor reading:", error);
        }
    };

    // Handle refresh sensor click
    const handleRefreshSensor = async (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        sensor: any
    ) => {
        const target = e.currentTarget as HTMLButtonElement;
        if (!target) return;
        
        const sensorId = `${sensor.ldsu_uuid}-${sensor.SAID}`;
        target.dataset.sensorId = sensorId;
        target.classList.add("refresh-state");
        
        try {
            await refreshOneSensor(sensor.ldsu_uuid, sensor.SAID);
        } finally {
            const element = document.querySelector(`.refresh-sensor[data-sensor-id="${sensorId}"]`) as HTMLButtonElement;
            if (element) {
                element.classList.remove("refresh-state");
                delete element.dataset.sensorId;
            }
        }
    };

    // Single-row controller refresh: per-device reading2 (bulk refresh still uses all-bus read).
    const refreshOneController = async (controllerUID: string, said: number) => {
        if (!id || !controllerUID) return;

        try {
            const { data, status } = await getSensorLastReading(id, controllerUID);
            if (!isHttpSuccess(status)) return;

            const parsed = getControllerStatusFromReading2(data, said);
            if (!parsed) return;

            const { status: updatedStatus, timestamp: updatedTimestamp } = parsed;

            setControllers((prevControllers: any[]) =>
                prevControllers.map((c: any) => {
                    if (c.UID === controllerUID && c.said === said) {
                        return {
                            ...c,
                            readings: {
                                value: updatedStatus,
                                status: updatedStatus,
                                timestamp: updatedTimestamp,
                            },
                        };
                    }
                    return c;
                })
            );
        } catch (error) {
            console.error("RoomDetails: Error refreshing controller status:", error);
        }
    };

    // Handle refresh controller click
    const handleRefreshController = async (
        e: React.MouseEvent,
        controllerName: string
    ) => {
        e.stopPropagation();

        const target = e.currentTarget as HTMLButtonElement | null;

        // Find the controller by name
        const controller = controllers.find((c: any) => c.name === controllerName);
        if (!controller?.UID) {
            console.error("RoomDetails: Controller not found for refresh");
            return;
        }

        if (target) {
            target.classList.add("refresh-state");
        }

        try {
            await refreshOneController(controller.UID, controller.said || 0);
        } finally {
            if (target) {
                target.classList.remove("refresh-state");
            }
        }
    };

    // Refresh all controller statuses
    const refreshAllControllerStatuses = async () => {
        if (!id || !controllers.length) return;
        
        setIsRefreshing(true);
        try {
            // Check all buses (1-3) to get all controller readings
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
            }
            const readingResults = await Promise.all(readingPromises);
            
            // Collect all device readings from all buses
            const allDevices: any[] = [];
            for (const result of readingResults) {
                if (isHttpSuccess(result.status) && result.data.devices) {
                    allDevices.push(...result.data.devices);
                }
            }
            
            // Build a lookup map for devices by UID
            const deviceByUID = new Map<string, any>();
            for (const device of allDevices) {
                if (device?.UID) {
                    deviceByUID.set(device.UID, device);
                }
            }

            // Helper to update a single controller with its latest reading (readings from getAllLastBusReading)
            const updateControllerWithLatestReading = (controller: any): any => {
                const device = deviceByUID.get(controller.UID);
                if (!device) {
                    return controller;
                }
                const updatedStatus = get(device.value, `[${controller.said || 0}]`, null);
                const updatedTimestamp = device.ts;
                return {
                    ...controller,
                    readings: {
                        value: updatedStatus,
                        status: updatedStatus,
                        timestamp: updatedTimestamp,
                    },
                };
            };

            // Update all controllers with their latest readings
            setControllers((prevControllers: any[]) =>
                prevControllers.map(updateControllerWithLatestReading)
            );
        } catch (error) {
            console.error("RoomDetails: Error refreshing all controller statuses:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    /* istanbul ignore next -- edit button is intentionally disabled in current UI flow */
    const toggleRoomNameEdit = () => {
        setIsRoomNameEditing(!isRoomNameEditing);
    };

    /* istanbul ignore next -- edit button is intentionally disabled in current UI flow */
    const toggleRoomNameUpdate = () => {
        setIsRoomNameEditing(false);
        if (room) {
            setRoom({ ...room, name: roomName });
        }
    };

    // Tab detection logic
    const getTabType = getSensorsControllersTabType;

    const handleRowClick = (row: any, tabType?: string) => {
        if (!validateAndRedirect()) {
            return;
        }
        switch (tabType) {
            case 'sensors': {
                const sensorPath = zoneIdFromParams
                    ? `/network-details/${id}/zone/${zoneIdFromParams}/room-details/${roomIdFromParams}/sensor-details/${row.ldsu_uuid}-${row.SAID}`
                    : `/network-details/${id}/room-details/${roomIdFromParams}/sensor-details/${row.ldsu_uuid}-${row.SAID}`;
                history.push({
                    pathname: sensorPath,
                    state: {
                        sensorDetails: row,
                        gatewayName: "Network Gateway",
                        portNumber: "1",
                        UUID: id,
                        sensorTimeRange: {
                            range: timeRange.range,
                            startDate: timeRange.startDate.valueOf(),
                            endDate: timeRange.endDate.valueOf(),
                        },
                    }
                });
                break;
            }
            case 'controllers':
                if (row.UID != null && row.said != null) {
                    const controllerPathId = `${row.UID}-${row.said}`;
                    if (zoneIdFromParams) {
                        history.push(`/network-details/${id}/zone/${zoneIdFromParams}/room-details/${roomIdFromParams}/controller-details/${controllerPathId}`);
                    } else {
                        history.push(`/network-details/${id}/room-details/${roomIdFromParams}/controller-details/${controllerPathId}`);
                    }
                }
                break;
            default:
                break;
        }
    };

    const rowToContext: NetworkRowToContext = useMemo(
        () => ({
            page: "room",
            networkId: id ?? "",
            roomId: roomIdFromParams ?? "",
            zoneId: zoneIdFromParams,
        }),
        [id, roomIdFromParams, zoneIdFromParams]
    );
    const getRowTo = useCallback(
        (row: any, tabType?: string) => getRowToForContext(rowToContext, row, tabType),
        [rowToContext]
    );

    // Preprocess data for controllers tab - merge status and lastActive
    const processTabData = (key: string, data: any[]) => {
        if (getTabType('controllers', data)) {
            return data.map((row: any) => ({
                ...row,
                status: `${row.status} ${row.lastActive}`,
            }));
        }
        return data;
    };

    
    const renderOnOffHighlighted = (text: string) => renderOnOffHighlightedCommon(text);


    // Show "Room not found" state when no room data
    if (!room) {
        return (
            <ContentWrapper isLoading={isPageLoading}>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={6} className="RoomDetails-header">
                                <Row>
                                    <Col>
                                        <h5 className="page-title">{customTitle || "Room Details"}</h5>
                                    </Col>
                                    {showBreadcrumbs && (
                                        <Col sm="12">
                                            <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["room-details"]} />
                                        </Col>
                                    )}
                                </Row>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={8}>
                                <div className="room-not-found-panel" style={{
                                    backgroundColor: '#2f3a51',
                                    borderRadius: '8px',
                                    padding: '24px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{
                                        color: '#ff3860',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px'
                                    }}>
                                        {isPageLoading ? 'Loading room data...' : 'Room not found.'}
                                    </div>
                                    {!isPageLoading && (
                                    <div style={{ color: '#a0a0a0' }}>
                                        The requested room could not be found in this network.
                                    </div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={4}>
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1" style={{ color: '#ffffff' }}>Status</h5>
                                            <div style={{ textAlign: "right" }}>
                                                <span
                                                    style={{
                                                        color: "#ff3860",
                                                        marginRight: 8,
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    &#9679;
                                                </span>
                                                <span style={{ color: '#ffffff' }}>Not found</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    // Helper to get default column keys for each tab type when no data is available
    const getDefaultColumnKeys = (tabKey: string): string[] => {
        switch (tabKey) {
            case 'sensors':
                return ['name', 'type', 'reading', 'minmax', 'lastactive', 'alert'];
            case 'controllers':
                return ['name', 'type', 'status'];
            default:
                return [];
        }
    };

    // Helper to get data source for column generation (prioritize tabData, fallback to state)
    const getDataForColumns = (tabKey: string) => {
        // First try tabData (filtered data)
        if (tabData[tabKey] && tabData[tabKey].length > 0) {
            return tabData[tabKey];
        }
        // Fallback to state variables (unfiltered data)
        let stateData: any[] = [];
        if (tabKey === 'sensors') stateData = processedSensors;
        else if (tabKey === 'controllers') stateData = processedControllers;
        
        if (stateData && Array.isArray(stateData) && stateData.length > 0) {
            return stateData;
        }
        // If no data, create a sample object with default keys for this tab type
        const defaultKeys = getDefaultColumnKeys(tabKey);
        if (defaultKeys.length > 0) {
            const sampleObject: any = {};
            for (const key of defaultKeys) {
                sampleObject[key] = null;
            }
            return [sampleObject];
        }
        return null;
    };

    const shouldIncludeColumn = (tabKey: string, col: string) => {
        const lower = col.toLowerCase();

            // Hide lastActive column for controllers tab since it's merged into status
            if (tabKey === 'controllers' &&
            (lower === 'lastactive' || lower.includes('lastactive') ||
             lower === 'uid' || lower === 'said')) {
                return false;
            }

        if (tabKey === 'sensors') {
            // Hide ldsu_uuid and SAID columns for sensors tab
            if (lower === 'ldsu_uuid' || lower === 'said') {
                return false;
            }

            // Hide minmax column (lowercase) if minMax (camelCase) already exists (to avoid duplicate min/max columns)
            // if (lower === 'minmax' && col !== 'minMax') {
            //     return false;
            // }

            // For sensors tab, only show name, type, reading, minMax, lastActive, alert
            const allowedColumns = ['name', 'type', 'reading', 'minmax', 'lastactive', 'alert'];
            return allowedColumns.includes(lower);
        }

        return true;
    };

    const buildColumnLabel = (col: string, tabKey: string): React.ReactNode =>
        getNetworkColumnLabel({
            col,
            tabKey,
            tabLabels,
            isRefreshing,
            refreshIcon,
            onRefreshSensors: refreshAllSensorReadings,
            onRefreshControllers: refreshAllControllerStatuses,
        });

    // Reusable function to generate columns from data
    const generateColumnsFromData = (data: any[], tabKey: string) => {
        if (!data || data.length === 0) return [];

        const rawColumns = Object.keys(data[0]);

        const filteredColumns = rawColumns.filter((col: string) =>
            shouldIncludeColumn(tabKey, col)
        );

        const columns = filteredColumns.map((col) => {
            const label = buildColumnLabel(col, tabKey);
            const render = buildColumnRenderHelper(col, tabKey, {
                handleRowClick,
                getRowTo,
                handleRefreshSensor,
                renderOnOffHighlighted,
                createRefreshControllerHandler,
                returnCheckboxStatus,
                returnDisabledStatus,
                createAlertOnChangeHandler
            });

            const lowerCol = col.toLowerCase();
            const isSensorTab = tabKey === "sensors";
            let sortKey: string | undefined;

            if (isSensorTab) {
                    if (lowerCol === "reading") {
                        sortKey = "readingSortValue";
                    } else if (lowerCol === "minmax" || lowerCol.includes("minmax")) {
                        sortKey = "minMaxSortValue";
                    } else if (lowerCol === "lastactive" || lowerCol.includes("lastactive")) {
                    sortKey = "readings.timestamp";
                } else if (lowerCol === "alert") {
                    sortKey = "alertSortValue";
                }
            } else if (tabKey === "controllers") {
                if (lowerCol === "name" || lowerCol === "type" || lowerCol === "status") {
                    sortKey = col;
                }
            }

            return {
                label,
                key: col,
                render,
                sortKey,
            };
        });

        return columns;
    };

    const tabData: { [key: string]: any[] } = {
        sensors: filteredSensorList.length > 0 ? filteredSensorList : processedSensors,
        controllers: processTabData('controllers', processedControllers),
    };

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={6} className="RoomDetails-header">
                            <Row>
                                <Col>
                                    <h5 className="page-title">{customTitle || room.name}</h5>
                                </Col>
                                {showBreadcrumbs && (
                                    <Col sm="12">
                                        <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["room-details"]} />
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={6}>
                            <div className="room-summary rounded shadow-sm">
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1">Status of Room</h5>
                                            <div style={{ textAlign: "right" }}>
                                                {(() => {
                                                    const isOn = room.status === "On";
                                                    const isOff = room.status === "Off";
                                                    const dotColor = isOn ? "#00d1b2" : "#ff3860";
                                                    let label = "Unknown";
                                                    if (isOff) {
                                                        label = "Off";
                                                    } else if (isOn) {
                                                        label = "On";
                                                    }

                                                    return (
                                                        <>
                                                            <span
                                                                style={{
                                                                    color: dotColor,
                                                                    marginRight: 8,
                                                                }}
                                                            >
                                                                &#9679;
                                                            </span>
                                                            {label}{" "}
                                                            <span style={{ color: "#828fab" }}>
                                                                {renderDateTime(room.timestamp)}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                                <IconButton
                                                    disableRipple
                                                    className={
                                                        isRefreshing
                                                            ? "mr-2 ml-2 bigRefresh refresh-animation"
                                                            : "mr-2 ml-2 bigRefresh"
                                                    }
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                    aria-label="Refresh room status"
                                                    onClick={() => {
                                                        updateRoomStatus();
                                                    }}
                                                    onKeyDown={() => updateRoomStatus()}
                                                >
                                                    <img
                                                        src={refreshIcon}
                                                        alt="refresh"
                                                    />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="room-info-group">
                                    <div>
                                        <Form>
                                            <div className="form-box mb-2">
                                                <Form.Row>
                                                    <Col md="12">
                                                        <Form.Label>
                                                            Room Name
                                                        </Form.Label>
                                                    </Col>
                                                    <Col
                                                        lg={10}
                                                        xs={12}
                                                        className="mb-2"
                                                    >
                                                        <Form.Group className="mb-0">
                                                            <Form.Control
                                                                type="text"
                                                                required
                                                                aria-label="room-name"
                                                                defaultValue={roomName}
                                                                disabled={!isRoomNameEditing}
                                                                onChange={(e) => {
                                                                    setRoomName(e.target.value);
                                                                }}
                                                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        toggleRoomNameUpdate();
                                                                    }
                                                                }}
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={2} xs="12">
                                                        {isRoomNameEditing ? (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                onClick={toggleRoomNameUpdate}
                                                            >
                                                                Update
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                disabled={true}
                                                                onClick={toggleRoomNameEdit}
                                                            >
                                                                EDIT
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Form.Row>
                                            </div>
                                        </Form>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} className="d-flex flex-column">
                            <div className="form-box room-summary rounded shadow-sm" style={{ padding: '0.5rem 30px;' }}>
                                <div className="room-info-group">
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Room UUID</h6>
                                            <h6 className="ginfo float-right">{room?.uuid || '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">No. of Sensors</h6>
                                            <h6 className="ginfo float-right">{room.sensors ?? '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">No. of Actuators</h6>
                                            <h6 className="ginfo float-right">{room.controllers ?? '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Network Name</h6>
                                            <h6 className="ginfo float-right">{network?.name || '-'}</h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <div className="page-tabs roomdetails-tabs mt-3">
                                <Tab.Container
                                    id="room-details-tabs"
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k || "sensors")}
                                >
                                    <Row>
                                        <Col sm={12}>
                                            <Nav className="flex-nowrap" style={{ whiteSpace: 'nowrap' }}>
                                                {tabKeys.map((key) => (
                                                    <Nav.Item key={key} style={{ width: '300px' }}>
                                                        <Nav.Link eventKey={key}>
                                                            <h6>
                                                                {tabLabels[key]}{" "}
                                                                <span className="count">
                                                                    {tabData[key] ? tabData[key].length : 0}
                                                                </span>
                                                            </h6>
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                ))}
                                            </Nav>
                                        </Col>
                                        <Col sm={12}>
                                            <Tab.Content>
                                                {tabKeys.map((key) => (
                                                    <Tab.Pane key={key} eventKey={key}>
                                                        <div style={{ borderRadius: 8, padding: 0 }}>
                                                            {key === 'sensors' && IS_BASIC_PLAN === false && (
                                                                <WalletAlertComponent showWarningWhen="LowBalance" />
                                                            )}
                                                            <TabTableWithSearch
                                                                data={tabData[key] || []}
                                                                emptyMessage={(() => {
                                                                    if (key === 'sensors') {
                                                                        return 'No Sensors to display';
                                                                    }
                                                                    if (key === 'controllers') {
                                                                        return 'No Actuators to display';
                                                                    }
                                                                    return 'No data to display';
                                                                })()}
                                                                columns={
                                                                    (() => {
                                                                        // Try to get data from tabData first, then fallback to state
                                                                        const dataForColumns = getDataForColumns(key);
                                                                        if (dataForColumns) {
                                                                            return generateColumnsFromData(dataForColumns, key);
                                                                        }
                                                                        // If no data available at all, return empty array (no headers will show)
                                                                        return [];
                                                                    })()
                                                                }
                                                                placeholder={`Search...`}
                                                                onRowClick={handleRowClick}
                                                                getRowTo={getRowTo}
                                                                tabType={key}
                                                                showArrowColumn={true}
                                                                customRenderers={
                                                                    getTabType('sensors', tabData[key] || []) ? {
                                                                        sensor_name: (value: any, row: any) =>
                                                                            createSensorNameRenderer('sensors', handleRowClick, getRowTo)(row),
                                                                        reading: (value: any, row: any) =>
                                                                            renderSensorReadingCell({ ...row, reading: value || '-' }, 'reading'),
                                                                        minMax: (value: any, row: any) =>
                                                                            renderSensorMinMaxCell({ ...row, minMax: value || '-' }, 'minMax'),
                                                                        lastactive: (value: any, row: any) =>
                                                                            renderLastActiveCell(
                                                                                { ...row, lastActive: value },
                                                                                'lastActive',
                                                                                'sensors',
                                                                                handleRefreshSensor
                                                                            ),
                                                                        alert: (value: any, row: any) =>
                                                                            renderAlertCell(
                                                                                row,
                                                                                returnCheckboxStatus,
                                                                                returnDisabledStatus,
                                                                                createAlertOnChangeHandler
                                                                        )
                                                                    } : {}
                                                                }
                                                                customFilters={renderSensorsControllersFilters({
                                                                    tabKey: key,
                                                                    sensorTypes,
                                                                    actuatorTypes,
                                                                    selectedSensorTypes,
                                                                    setSelectedSensorTypes,
                                                                    selectedActuatorTypes,
                                                                    setSelectedActuatorTypes,
                                                                })}
                                                            />
                                                        </div>
                                                    </Tab.Pane>
                                                ))}
                                            </Tab.Content>
                                        </Col>
                                    </Row>
                                </Tab.Container>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <LoadingModal showModal={isAlertLoading} />
        </ContentWrapper>
    );
};

// Helper to render tab‑specific filter controls outside of the RoomDetails component
export default RoomDetails; 